import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { collection, query, where, getDocs, orderBy, deleteDoc, doc, updateDoc, getDoc, setDoc, onSnapshot, addDoc, serverTimestamp, writeBatch } from "firebase/firestore";
import { updateProfile } from "firebase/auth";
import Header from "../components/Header";
import PostCard from "../components/PostCard";
import "../index.css";

export default function Profile() {
  const [user, setUser] = useState(null);
  const [myPosts, setMyPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 프로필 수정 상태
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editPhoto, setEditPhoto] = useState(""); // Base64 string will be stored here
  const [file, setFile] = useState(null);
  // 친구/DM 상태
  const [friends, setFriends] = useState([]);
  const [friendMethod, setFriendMethod] = useState("code"); // code | email | uid | name
  const [friendInput, setFriendInput] = useState("");
  const [myFriendCode, setMyFriendCode] = useState("");
  const [chatWith, setChatWith] = useState(null); // { uid, displayName, email }
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [unsubMessages, setUnsubMessages] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    // Auth 상태 확인
    const unsubscribeAuth = auth.onAuthStateChanged(async (currentUser) => {
      if (!currentUser) {
        navigate("/");
        return;
      }

      // 1. Auth 정보로 우선 초기화 (화면 깜빡임 방지)
      // 단, 기존에 로드된 정보가 있다면 유지
      if (!user) {
         setUser(currentUser);
         setEditName(currentUser.displayName || "");
      }

      // 2. Firestore 실시간 구독 (Auth보다 DB 우선)
      const userRef = doc(db, "users", currentUser.uid);
      const unsubscribeSnapshot = onSnapshot(userRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const finalName = data.displayName || currentUser.displayName;
          // photoBase64가 있으면 사용, 없으면 Auth photoURL
          const finalPhoto = data.photoBase64 || currentUser.photoURL;
          const code = data.friendCode || "";

          // 상태 업데이트
          setUser((prev) => ({ ...prev, ...currentUser, displayName: finalName, photoURL: finalPhoto }));
          setEditName(finalName || "");
          setEditPhoto(finalPhoto || "");
          setMyFriendCode(code);
        } else {
           // 문서가 없는 경우 Auth 정보 사용
           setUser(currentUser);
           setEditName(currentUser.displayName || "");
           setEditPhoto(currentUser.photoURL || "");
        }
      }, (error) => {
        console.error("Error fetching user profile:", error);
      });

      fetchMyPosts(currentUser.uid);
      fetchFriends(currentUser.uid);
      
      // Cleanup snapshot listener when auth state changes or component unmounts
      return () => unsubscribeSnapshot();
    });

    return () => unsubscribeAuth();
  }, [navigate]);

  const fetchMyPosts = async (uid) => {
    setError(null);
    try {
      // 1. 정렬된 쿼리 시도
      const q = query(
        collection(db, "posts"),
        where("authorId", "==", uid),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);
      const posts = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMyPosts(posts);
    } catch (err) {
      console.error("Error fetching my posts (sorted):", err);
      
      // 2. 인덱스 에러 발생 시 fallback: 정렬 없이 가져오기
      if (err.code === 'failed-precondition') {
        try {
          console.log("Falling back to unsorted query...");
          const qFallback = query(
            collection(db, "posts"),
            where("authorId", "==", uid)
          );
          const querySnapshot = await getDocs(qFallback);
          const posts = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          // 클라이언트 측에서 정렬 (임시 해결책)
          posts.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          
          setMyPosts(posts);
          setError("⚠️ Firestore 인덱스가 필요합니다. 콘솔의 링크를 클릭하여 인덱스를 생성해주세요. (현재는 임시 정렬됨)");
        } catch (fallbackErr) {
          console.error("Error fetching my posts (fallback):", fallbackErr);
          setError(`글을 불러오는 중 오류가 발생했습니다: ${fallbackErr.message}`);
        }
      } else {
        setError(`글을 불러오는 중 오류가 발생했습니다: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!auth.currentUser) return;
    try {
      let photoBase64 = editPhoto;

      if (file) {
      // 용량 제한 체크 (Firestore 문서 1MB 제한 고려, 안전하게 700KB로 제한)
      if (file.size > 700 * 1024) {
        alert("이미지 파일이 너무 큽니다. 700KB 이하의 이미지를 선택해주세요.\n(Firestore 저장 용량 제한)");
        return;
      }

      // 파일을 Base64로 변환
        const reader = new FileReader();
        photoBase64 = await new Promise((resolve, reject) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = (e) => reject(e);
          reader.readAsDataURL(file);
        });
      }

      // 1. Auth 프로필 업데이트 (이름만)
      await updateProfile(auth.currentUser, {
        displayName: editName,
        // photoURL은 길이 제한 때문에 Base64를 넣을 수 없을 가능성이 높음.
        // 따라서 photoURL은 비워두거나 기존 값을 유지하고, 실제 이미지는 Firestore에 저장함.
      });

      // 2. Firestore에 Base64 이미지 저장 (없으면 생성, 있으면 병합)
      const userRef = doc(db, "users", auth.currentUser.uid);
      await setDoc(userRef, {
        displayName: editName,
        photoBase64: photoBase64, // Base64 문자열 저장
        email: auth.currentUser.email, // 문서가 새로 생성될 경우를 대비해 기본 정보 추가
        uid: auth.currentUser.uid,
        lastUpdated: serverTimestamp()
      }, { merge: true });

      // 3. 사용자가 작성한 모든 게시글의 프로필 정보 업데이트
      try {
        const postsQuery = query(
          collection(db, "posts"),
          where("authorId", "==", auth.currentUser.uid)
        );
        const postsSnap = await getDocs(postsQuery);

        if (!postsSnap.empty) {
          const batch = writeBatch(db);
          let count = 0;
          postsSnap.forEach((docSnap) => {
            const postRef = doc(db, "posts", docSnap.id);
            batch.update(postRef, {
              author: editName,
              authorPhoto: photoBase64
            });
            count++;
          });
          await batch.commit();
          console.log(`${count}개의 게시글 프로필 업데이트 완료`);
        }
      } catch (postErr) {
        console.error("Error updating posts profile:", postErr);
        // 게시글 업데이트 실패해도 프로필 수정은 성공한 것이므로 경고만 로그에 남김
      }

      // 상태 업데이트
      setEditPhoto(photoBase64);
      setUser({ ...auth.currentUser, displayName: editName, photoURL: photoBase64 }); // UI 즉시 반영
      setIsEditing(false);
      setFile(null);
      alert("프로필이 업데이트되었습니다.");
      
      // 페이지 새로고침하여 변경사항 반영 (헤더 등)
      window.location.reload();
      
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("프로필 업데이트 실패: " + error.message);
    }
  };

  const fetchFriends = async (uid) => {
    try {
      const q = query(collection(db, "users", uid, "friends"));
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setFriends(list);
    } catch (err) {
      console.error("Error fetching friends:", err);
    }
  };

  const addFriend = async () => {
    if (!friendInput.trim() || !user) return;
    try {
      let target;
      if (friendMethod === "email") {
        const qEmail = query(collection(db, "users"), where("email", "==", friendInput.trim()));
        const snap = await getDocs(qEmail);
        if (snap.empty) { alert("해당 이메일의 사용자를 찾을 수 없습니다."); return; }
        const d = snap.docs[0];
        target = { uid: d.id, ...d.data() };
      } else if (friendMethod === "uid") {
        const d = await getDoc(doc(db, "users", friendInput.trim()));
        if (!d.exists()) { alert("해당 UID의 사용자를 찾을 수 없습니다."); return; }
        target = { uid: d.id, ...d.data() };
      } else if (friendMethod === "name") {
        const qName = query(collection(db, "users"), where("displayName", "==", friendInput.trim()));
        const snap = await getDocs(qName);
        if (snap.empty) { alert("해당 닉네임의 사용자를 찾을 수 없습니다."); return; }
        const d = snap.docs[0];
        target = { uid: d.id, ...d.data() };
      } else { // code
        const qCode = query(collection(db, "users"), where("friendCode", "==", friendInput.trim().toUpperCase()));
        const snap = await getDocs(qCode);
        if (snap.empty) { alert("해당 코드의 사용자를 찾을 수 없습니다."); return; }
        const d = snap.docs[0];
        target = { uid: d.id, ...d.data() };
      }
      if (target.uid === user.uid) {
        alert("자기 자신은 친구로 추가할 수 없습니다.");
        return;
      }
      // 상호 친구 추가
      await setDoc(doc(db, "users", user.uid, "friends", target.uid), {
        friendUid: target.uid,
        displayName: target.displayName || target.email,
        email: target.email,
        createdAt: serverTimestamp(),
      });
      await setDoc(doc(db, "users", target.uid, "friends", user.uid), {
        friendUid: user.uid,
        displayName: user.displayName || user.email,
        email: user.email,
        createdAt: serverTimestamp(),
      });
      setFriendInput("");
      fetchFriends(user.uid);
      alert("친구 추가 완료");
    } catch (err) {
      console.error("Error adding friend:", err);
      alert("친구 추가 실패: " + err.message);
    }
  };

  const getThreadId = (uidA, uidB) => {
    const [a, b] = [uidA, uidB].sort();
    return `${a}_${b}`;
  };

  const openChatWith = async (friend) => {
    if (!user) return;
    try {
      // 기존 메시지 구독 해제
      if (unsubMessages) {
        unsubMessages();
        setUnsubMessages(null);
      }
      setChatWith(friend);
      const threadId = getThreadId(user.uid, friend.friendUid || friend.uid);
      const threadRef = doc(db, "dmThreads", threadId);
      
      // 존재 여부와 관계없이 생성/병합 (읽기 권한 문제를 피하기 위해 즉시 생성)
      // rules에서 request.resource.data.participants 체크하므로 반드시 포함해야 함
      await setDoc(threadRef, {
        participants: [user.uid, friend.friendUid || friend.uid].sort(),
        lastUpdated: serverTimestamp(), // 정렬용으로 사용 가능
      }, { merge: true });

      const msgQuery = query(collection(db, "dmThreads", threadId, "messages"), orderBy("createdAt", "asc"));
      const unsub = onSnapshot(msgQuery, (snapshot) => {
        const msgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setMessages(msgs);
      });
      setUnsubMessages(() => unsub);
    } catch (err) {
      console.error("Error opening chat:", err);
      alert("채팅방을 여는 중 오류가 발생했습니다: " + err.message);
      setChatWith(null); // 에러 시 채팅창 닫기
    }
  };

  const sendMessage = async () => {
    if (!user || !chatWith || !newMessage.trim()) return;
    try {
      const threadId = getThreadId(user.uid, chatWith.friendUid || chatWith.uid);
      await addDoc(collection(db, "dmThreads", threadId, "messages"), {
        senderId: user.uid,
        text: newMessage.trim(),
        createdAt: serverTimestamp(),
      });
      setNewMessage("");
    } catch (err) {
      console.error("Error sending message:", err);
      alert("메시지 전송 실패: " + err.message);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!user || !chatWith) return;
    if (!window.confirm("메시지를 삭제하시겠습니까?")) return;
    try {
      const threadId = getThreadId(user.uid, chatWith.friendUid || chatWith.uid);
      await deleteDoc(doc(db, "dmThreads", threadId, "messages", messageId));
    } catch (err) {
      console.error("Error deleting message:", err);
      alert("메시지 삭제 실패: " + err.message);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await deleteDoc(doc(db, "posts", postId));
      setMyPosts(prev => prev.filter(post => post.id !== postId));
      alert("게시글이 삭제되었습니다.");
    } catch (err) {
      console.error("Error deleting post:", err);
      alert("게시글 삭제 실패: " + err.message);
    }
  };

  const handleDeleteFriend = async (friendUid) => {
    if (!window.confirm("정말로 이 친구를 삭제하시겠습니까? 대화 내용(DM)은 유지되지만 친구 목록에서 사라집니다.")) return;
    try {
      // 1. 내 친구 목록에서 삭제
      await deleteDoc(doc(db, "users", user.uid, "friends", friendUid));
      // 2. 상대방 친구 목록에서 삭제 (상호 삭제)
      // 상대방 권한이 없어도 rules에서 (auth.uid == friendUid || auth.uid == userId)로 삭제 허용했으므로 가능
      await deleteDoc(doc(db, "users", friendUid, "friends", user.uid));
      
      setFriends(prev => prev.filter(f => f.friendUid !== friendUid));
      
      // 만약 현재 채팅 중인 친구라면 채팅 닫기
      if (chatWith?.friendUid === friendUid || chatWith?.uid === friendUid) {
        setChatWith(null);
        setMessages([]);
        if (unsubMessages) { unsubMessages(); setUnsubMessages(null); }
      }
      alert("친구가 삭제되었습니다.");
    } catch (err) {
      console.error("Error deleting friend:", err);
      alert("친구 삭제 실패: " + err.message);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
  };

  if (loading) {
    return <div style={{ textAlign: "center", marginTop: "50px" }}>로딩 중...</div>;
  }

  return (
    <>
      <Header />
      <main style={{ maxWidth: "768px", margin: "0 auto", padding: "32px 16px" }}>
        {/* 프로필 섹션 */}
        <div className="profile-header" style={{ display: "flex", flexDirection: "column", gap: "24px", marginBottom: "48px", borderBottom: "1px solid #eee", paddingBottom: "32px" }}>
          
          <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
            <img
              src={user?.photoURL || "https://via.placeholder.com/150"}
              alt="Profile"
              style={{ width: "128px", height: "128px", borderRadius: "50%", objectFit: "cover" }}
            />
            <div>
              <h2 style={{ fontSize: "2rem", fontWeight: "bold", margin: "0 0 8px 0" }}>{user?.displayName || "사용자"}</h2>
              {!isEditing && (
                <button 
                  onClick={() => setIsEditing(true)}
                  style={{ marginTop: "16px", padding: "8px 16px", cursor: "pointer", background: "#f1f3f5", border: "none", borderRadius: "4px" }}
                >
                  프로필 수정
                </button>
              )}
            </div>
          </div>

          {/* 프로필 수정 폼 */}
          {isEditing && (
            <div style={{ background: "#f8f9fa", padding: "20px", borderRadius: "8px", marginTop: "10px" }}>
              <div style={{ marginBottom: "12px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>이름</label>
                <input 
                  value={editName} 
                  onChange={(e) => setEditName(e.target.value)}
                  style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ddd" }}
                />
              </div>
              <div style={{ marginBottom: "12px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>프로필 이미지</label>
                <input 
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files[0])}
                  style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ddd" }}
                />
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <button 
                  onClick={handleUpdateProfile}
                  className="btn-primary"
                  style={{ padding: "8px 16px" }}
                >
                  저장
                </button>
                <button 
                  onClick={() => setIsEditing(false)}
                  style={{ padding: "8px 16px", cursor: "pointer", background: "#fff", border: "1px solid #ddd", borderRadius: "4px" }}
                >
                  취소
                </button>
              </div>
            </div>
          )}

        </div>

        {/* 친구 & 1:1 채팅 섹션 */}
        <div style={{ marginTop: "48px", paddingTop: "32px", borderTop: "1px solid #eee" }}>
          <h3 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "16px" }}>친구 & 1:1 채팅</h3>
          
          {/* 채팅방 모드일 때 */}
          {chatWith ? (
            <div style={{ border: "1px solid #eee", borderRadius: "8px", overflow: "hidden", display: "flex", flexDirection: "column", height: "600px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
              {/* 채팅 헤더 */}
              <div style={{ padding: "16px", background: "#f8f9fa", borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <button 
                    onClick={() => { setChatWith(null); setMessages([]); if (unsubMessages) { unsubMessages(); setUnsubMessages(null);} }}
                    style={{ background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer", padding: "4px 8px" }}
                  >
                    ←
                  </button>
                  <h4 style={{ margin: 0, fontSize: "1.1rem" }}>{(chatWith.displayName || chatWith.email) + " 님과의 대화"}</h4>
                </div>
                <button 
                   onClick={() => { setChatWith(null); setMessages([]); if (unsubMessages) { unsubMessages(); setUnsubMessages(null);} }}
                   style={{ background: "#fff", border: "1px solid #ddd", padding: "6px 12px", borderRadius: "4px", cursor: "pointer" }}
                >
                  목록으로
                </button>
              </div>
              
              {/* 메시지 영역 */}
              <div style={{ flex: 1, overflowY: "auto", padding: "16px", background: "#fff", display: "flex", flexDirection: "column", gap: "12px" }}>
                {messages.length === 0 ? (
                  <div style={{ textAlign: "center", color: "#868e96", marginTop: "40px" }}>
                    대화 내용이 없습니다.<br/>메시지를 보내보세요!
                  </div>
                ) : (
                  messages.map(m => (
                    <div key={m.id} style={{ alignSelf: m.senderId === user.uid ? "flex-end" : "flex-start", maxWidth: "70%" }}>
                       <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", flexDirection: m.senderId === user.uid ? "row" : "row-reverse" }}>
                         {/* 내 메시지일 경우 삭제 버튼 표시 */}
                         {m.senderId === user.uid && (
                           <button
                             onClick={() => handleDeleteMessage(m.id)}
                             style={{
                               background: "none",
                               border: "none",
                               color: "#fa5252",
                               cursor: "pointer",
                               fontSize: "0.8rem",
                               padding: "0 4px",
                               marginBottom: "4px"
                             }}
                             title="삭제"
                           >
                             ✕
                           </button>
                         )}
                         <div style={{ 
                           background: m.senderId === user.uid ? "#339af0" : "#f1f3f5", 
                           color: m.senderId === user.uid ? "#fff" : "#333",
                           padding: "10px 14px", 
                           borderRadius: "16px",
                           borderTopRightRadius: m.senderId === user.uid ? "4px" : "16px",
                           borderTopLeftRadius: m.senderId === user.uid ? "16px" : "4px",
                           wordBreak: "break-word",
                           boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                         }}>
                           {m.text}
                         </div>
                       </div>
                       <div style={{ fontSize: "0.75rem", color: "#adb5bd", marginTop: "4px", textAlign: m.senderId === user.uid ? "right" : "left" }}>
                         {m.createdAt ? formatDate(m.createdAt) : "전송 중..."}
                       </div>
                    </div>
                  ))
                )}
              </div>

              {/* 입력 영역 */}
              <div style={{ padding: "16px", background: "#f8f9fa", borderTop: "1px solid #eee", display: "flex", gap: "10px" }}>
                <input 
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") sendMessage(); }}
                  placeholder="메시지를 입력하세요..."
                  style={{ flex: 1, padding: "12px", borderRadius: "24px", border: "1px solid #ddd", outline: "none" }}
                />
                <button 
                  className="btn-primary" 
                  onClick={sendMessage} 
                  style={{ padding: "0 24px", borderRadius: "24px", fontWeight: "bold" }}
                >
                  전송
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* 친구 관리 UI (리스트 모드) */}
              <div style={{ marginBottom: "24px", padding: "20px", background: "#f8f9fa", borderRadius: "8px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "1rem", color: "#495057" }}>친구 추가</h4>
                <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
                  <select value={friendMethod} onChange={(e) => setFriendMethod(e.target.value)} style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ddd" }}>
                    <option value="code">코드</option>
                    <option value="email">이메일</option>
                    <option value="uid">UID</option>
                    <option value="name">닉네임</option>
                  </select>
                  <input 
                    placeholder={friendMethod === "code" ? "친구 코드 (예: ABC123)" 
                                : friendMethod === "email" ? "친구 이메일" 
                                : friendMethod === "uid" ? "친구 UID" 
                                : "친구 닉네임"}
                    value={friendInput}
                    onChange={(e) => setFriendInput(e.target.value)}
                    style={{ flex: 1, minWidth: "200px", padding: "8px", borderRadius: "4px", border: "1px solid #ddd" }}
                  />
                  <button className="btn-primary" onClick={addFriend} style={{ padding: "8px 16px" }}>
                    추가
                  </button>
                </div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center", fontSize: "0.9rem", color: "#495057" }}>
                   <span>내 친구 코드: <b>{myFriendCode || "생성중"}</b></span>
                   {myFriendCode && (
                    <button 
                      onClick={() => { navigator.clipboard.writeText(myFriendCode); alert("복사되었습니다!"); }}
                      style={{ padding: "2px 8px", border: "1px solid #ced4da", borderRadius: "4px", background: "#fff", cursor: "pointer", fontSize: "0.8rem" }}
                    >
                      복사
                    </button>
                   )}
                </div>
              </div>

              {/* 친구 목록 헤더 */}
              <div style={{ 
                display: "flex", 
                padding: "12px 16px", 
                borderBottom: "2px solid #333", 
                fontWeight: "bold", 
                color: "#495057",
                fontSize: "0.9rem",
                marginTop: "16px"
              }}>
                <div style={{ flex: 1 }}>친구 이름</div>
                <div style={{ width: "100px", textAlign: "center" }}>추가일</div>
                <div style={{ width: "100px", textAlign: "right" }}>관리</div>
              </div>

              {/* 친구 목록 아이템 */}
              <div style={{ display: "flex", flexDirection: "column" }}>
                {friends.length === 0 ? (
                  <p style={{ textAlign: "center", padding: "48px 0", color: "#868e96" }}>
                    아직 친구가 없습니다. 위에서 친구를 추가해보세요!
                  </p>
                ) : (
                  friends.map(f => (
                    <div 
                      key={f.friendUid} 
                      onClick={() => openChatWith(f)}
                      style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        padding: "16px", 
                        borderBottom: "1px solid #eee", 
                        cursor: "pointer",
                        transition: "background-color 0.2s"
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                    >
                      {/* 친구 정보 */}
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
                        <span style={{ fontWeight: "bold", fontSize: "1rem", color: "#333" }}>{f.displayName}</span>
                      </div>
                      
                      {/* 추가일 */}
                      <div style={{ width: "100px", textAlign: "center", fontSize: "0.85rem", color: "#868e96" }}>
                        {f.createdAt ? formatDate(f.createdAt) : "-"}
                      </div>
                      
                      {/* 관리 버튼 */}
                      <div style={{ width: "100px", display: "flex", justifyContent: "flex-end", gap: "8px" }} onClick={(e) => e.stopPropagation()}>
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleDeleteFriend(f.friendUid); }}
                          style={{ 
                            padding: "6px 12px", 
                            border: "1px solid #fa5252", 
                            color: "#fa5252", 
                            background: "#fff", 
                            borderRadius: "4px", 
                            cursor: "pointer",
                            fontSize: "0.85rem"
                          }}
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
        {/* 서버 규칙 섹션 */}
        <div style={{ marginBottom: "48px", padding: "24px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
          <h3 style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "16px", color: "#495057" }}>🏛️ 서버 규칙 헌장</h3>
          
          <div style={{ color: "#495057", lineHeight: "1.6", fontSize: "0.95rem" }}>
            <h4 style={{ fontWeight: "bold", marginTop: "16px", marginBottom: "8px" }}>제1조 금지 및 제한 행위</h4>
            <ul style={{ paddingLeft: "20px", margin: 0 }}>
              <li style={{ marginBottom: "8px" }}>
                <b>제1항</b> 성적 발언, 패드립, 정치적 발언 금지 (경고 3~7회)
              </li>
              <li style={{ marginBottom: "8px" }}>
                <b>제2항</b> 관리자 승인 없는 외부 디스코드 초대 링크 공유 금지 (경고 2~4회)
              </li>
              <li style={{ marginBottom: "8px" }}>
                <b>제3항</b> 일상적 욕설 허용 (X발, X신 등..) 단 명백히 모욕적인 욕설 금지 (경고 1~2회)
              </li>
              <li style={{ marginBottom: "8px" }}>
                <b>제4항</b> 시비 유도 및 갈등 조성 행위 금지 (경고 1~4회)
              </li>
              <li style={{ marginBottom: "8px" }}>
                <b>제5항</b> 관리자 권력 남용 금지 (경고 4~7회)
              </li>
              <li style={{ marginBottom: "8px" }}>
                <b>제6항</b> 과도한 언행(뇌절) 금지 (경고 1~3회)
              </li>
              <li style={{ marginBottom: "8px" }}>
                <b>제7항</b> 들낙(지속적인 입·퇴장 반복) 금지 (경고 2~4회)
              </li>
              <li style={{ marginBottom: "8px" }}>
                <b>제8항</b> 도배 행위 금지 (경고 1~3회)
              </li>
              <li style={{ marginBottom: "8px" }}>
                <b>제9항</b> 특정인 소외(울타리 친목) 행위 엄격 금지 (경고 5~7회)
              </li>
              <li style={{ marginBottom: "8px" }}>
                <b>제10항</b> 스팸·광고·홍보 링크/메시지 금지 (경고 4~6회)
              </li>
              <li style={{ marginBottom: "8px" }}>
                <b>제11항</b> 타인 개인정보 무단 공유·노출 금지 (경고 3~6회)
              </li>
              <li style={{ marginBottom: "8px" }}>
                <b>제12항</b> 금전·물품·역할 구걸 금지 (경고 1~2회)
              </li>
              <li style={{ marginBottom: "8px" }}>
                <b>제13항</b> 서버/구성원 위협 및 테러 행위 금지 (경고 2~4회)
              </li>
            </ul>

            <h4 style={{ fontWeight: "bold", marginTop: "24px", marginBottom: "8px" }}>제2조 관리자의 재량</h4>
            <p style={{ margin: 0 }}>
              명시되지 않은 상황 발생 시, 관리자는 사안의 경중/고의성/피해 정도를 고려하여 제재를 결정할 권한을 가집니다.
            </p>

            <h4 style={{ fontWeight: "bold", marginTop: "24px", marginBottom: "8px" }}>제3조 제재의 누적 및 추방 기준</h4>
            <p style={{ margin: 0 }}>
              경고는 누적 적용되며, <b>누적 경고 8회 도달 시 즉시 추방</b> 조치됩니다.
            </p>
          </div>
        </div>

        {/* 내 글 목록 섹션 */}
        <h3 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "24px" }}>내가 쓴 글 ({myPosts.length})</h3>
        
        {/* 에러 메시지 표시 */}
        {error && (
          <div style={{ backgroundColor: "#fff3cd", color: "#856404", padding: "12px", borderRadius: "8px", marginBottom: "24px" }}>
            {error}
          </div>
        )}

        {/* 게시판형 리스트 헤더 */}
        <div style={{ 
          display: "flex", 
          padding: "12px 16px", 
          borderBottom: "2px solid #333", 
          fontWeight: "bold", 
          color: "#495057",
          fontSize: "0.9rem"
        }}>
          <div style={{ flex: 1 }}>제목</div>
          <div style={{ width: "120px" }}>작성자</div>
          <div style={{ width: "100px", textAlign: "center" }}>날짜</div>
          <div style={{ width: "80px", textAlign: "right" }}>좋아요</div>
        </div>

        <div style={{ display: "flex", flexDirection: "column" }}>
          {myPosts.length === 0 ? (
            <p style={{ color: "#868e96", padding: "32px 0", textAlign: "center" }}>작성한 글이 없습니다.</p>
          ) : (
            myPosts.map((post) => (
              <PostCard
                key={post.id}
                id={post.id}
                title={post.title}
                author={post.author}
                authorPhoto={post.authorPhoto}
                date={formatDate(post.createdAt)}
                preview={post.content ? (post.content.length > 150 ? post.content.substring(0, 150) + "..." : post.content) : ""}
                likedBy={post.likedBy}
                isOwner={true}
                onDelete={handleDeletePost}
              />
            ))
          )}
        </div>
      </main>
    </>
  );
}
